import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  writeBatch,
  Timestamp,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../../../utils/init-firebase';
import { L10Note, COLLECTION_NAMES } from '../../types';

export interface SavedMeetingDisplay {
  meeting_date: string;
  meeting_timestamp: string;
  display_name: string;
}

export interface StructuredNotes {
  sectionNotes: Record<string, string>; // Main textarea notes per section
  individualNotes: Record<string, L10Note[]>; // Individual note entries per section
}

export function useL10Notes(meetingTimestamp: string | null) {
  const [notes, setNotes] = useState<L10Note[]>([]);
  const [structuredNotes, setStructuredNotes] = useState<StructuredNotes>({
    sectionNotes: {},
    individualNotes: {},
  });
  const [savedMeetings, setSavedMeetings] = useState<SavedMeetingDisplay[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMeetings, setLoadingMeetings] = useState(false);

  useEffect(() => {
    loadSavedMeetings();
  }, []);

  useEffect(() => {
    if (meetingTimestamp) {
      loadNotes(meetingTimestamp);
    } else {
      setNotes([]);
    }
  }, [meetingTimestamp]);

  const loadSavedMeetings = async () => {
    setLoadingMeetings(true);
    try {
      // Query all three collections to get unique meeting timestamps
      const [notesSnapshot, issuesSnapshot, metricsSnapshot] = await Promise.all([
        getDocs(query(
          collection(db, COLLECTION_NAMES.L10_NOTES),
          orderBy('meetingTimestamp', 'desc')
        )),
        getDocs(query(
          collection(db, COLLECTION_NAMES.MEETING_ISSUES),
          orderBy('meetingTimestamp', 'desc')
        )),
        getDocs(query(
          collection(db, COLLECTION_NAMES.MEETING_OFF_TRACK_METRICS),
          orderBy('meetingTimestamp', 'desc')
        )),
      ]);

      const allTimestamps = new Set<string>();
      const timestampToDate = new Map<string, string>();

      // Collect from l10_notes
      notesSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const timestamp = data.meetingTimestamp;
        if (timestamp) {
          const timestampStr = timestamp instanceof Timestamp 
            ? timestamp.toDate().toISOString() 
            : timestamp;
          allTimestamps.add(timestampStr);
          const date = (typeof data.meetingDate === 'string' ? data.meetingDate : null) || timestampStr.split('T')[0];
          timestampToDate.set(timestampStr, date);
        }
      });

      // Collect from meeting_issues
      issuesSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const timestamp = data.meetingTimestamp;
        if (timestamp) {
          const timestampStr = timestamp instanceof Timestamp 
            ? timestamp.toDate().toISOString() 
            : timestamp;
          allTimestamps.add(timestampStr);
          if (!timestampToDate.has(timestampStr)) {
            const date = timestampStr.split('T')[0];
            timestampToDate.set(timestampStr, date);
          }
        }
      });

      // Collect from meeting_off_track_metrics
      metricsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const timestamp = data.meetingTimestamp;
        if (timestamp) {
          const timestampStr = timestamp instanceof Timestamp 
            ? timestamp.toDate().toISOString() 
            : timestamp;
          allTimestamps.add(timestampStr);
          if (!timestampToDate.has(timestampStr)) {
            const date = timestampStr.split('T')[0];
            timestampToDate.set(timestampStr, date);
          }
        }
      });

      // Convert to array and format
      const uniqueMeetings = Array.from(allTimestamps)
        .sort((a, b) => b.localeCompare(a))
        .map((timestamp) => {
          const date = timestampToDate.get(timestamp) || timestamp.split('T')[0];
          return {
            meeting_date: date,
            meeting_timestamp: timestamp,
            display_name: formatMeetingDisplay(date, timestamp),
          };
        });

      setSavedMeetings(uniqueMeetings);
    } catch (error) {
      console.error('Error loading saved meetings:', error);
    } finally {
      setLoadingMeetings(false);
    }
  };

  const loadNotes = async (timestamp: string) => {
    setLoading(true);
    try {
      // Convert string timestamp to Timestamp for query
      const timestampDate = new Date(timestamp);
      const timestampObj = Timestamp.fromDate(timestampDate);

      const q = query(
        collection(db, COLLECTION_NAMES.L10_NOTES),
        where('meetingTimestamp', '==', timestampObj)
      );

      const snapshot = await getDocs(q);
      const notesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        meetingTimestamp: doc.data().meetingTimestamp,
        meetingDate: doc.data().meetingDate,
        createdAt: doc.data().createdAt,
        updatedAt: doc.data().updatedAt,
      })) as L10Note[];

      setNotes(notesData);

      // All notes are treated as individual notes (cards)
      const sectionNotes: Record<string, string> = {};
      const individualNotes: Record<string, L10Note[]> = {};

      notesData.forEach((note) => {
        // Group all notes by section as individual notes
        if (!individualNotes[note.section]) {
          individualNotes[note.section] = [];
        }
        individualNotes[note.section].push(note);
      });

      setStructuredNotes({ sectionNotes, individualNotes });
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveNotes = async (
    sectionNotes: Record<string, string>,
    individualNotes: Record<string, L10Note[]>,
    meetingDate: string,
    timestamp: string,
    skipReload: boolean = false
  ) => {
    try {
      const batch = writeBatch(db);
      const timestampObj = Timestamp.fromDate(new Date(timestamp));

      // Find and delete existing notes for this meeting
      const existingNotesQuery = query(
        collection(db, COLLECTION_NAMES.L10_NOTES),
        where('meetingTimestamp', '==', timestampObj)
      );
      const existingSnapshot = await getDocs(existingNotesQuery);
      existingSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });

      // Add all notes as individual notes (no distinction between main and individual)
      Object.values(individualNotes).flat().forEach((note) => {
        if (note.notes.trim()) {
          const newNoteRef = doc(collection(db, COLLECTION_NAMES.L10_NOTES));
          batch.set(newNoteRef, {
            section: note.section,
            notes: note.notes,
            meetingDate,
            meetingTimestamp: timestampObj,
            noteCategory: note.noteCategory || 'general',
            assignedUsers: note.assignedUsers || [],
            assignedDepartments: note.assignedDepartments || [],
            createdAt: note.createdAt || Timestamp.now(),
            updatedAt: Timestamp.now(),
          });
        }
      });

      // If no notes at all, add a placeholder
      if (Object.values(individualNotes).flat().length === 0) {
        const newNoteRef = doc(collection(db, COLLECTION_NAMES.L10_NOTES));
        batch.set(newNoteRef, {
          section: 'general',
          notes: '(No notes recorded)',
          meetingDate,
          meetingTimestamp: timestampObj,
          noteCategory: 'general',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }

      await batch.commit();

      if (skipReload) {
        await loadSavedMeetings();
      } else {
        await Promise.all([
          loadSavedMeetings(),
          loadNotes(timestamp)
        ]);
      }

      return true;
    } catch (error) {
      console.error('Error saving notes:', error);
      throw error;
    }
  };

  const addIndividualNote = async (
    section: string,
    note: string,
    category: 'general' | 'department_communication',
    meetingDate: string,
    timestamp: string,
    owners?: string[],
    departments?: string[]
  ) => {
    try {
      const timestampObj = Timestamp.fromDate(new Date(timestamp));
      
      await addDoc(collection(db, COLLECTION_NAMES.L10_NOTES), {
        section,
        notes: note,
        meetingDate,
        meetingTimestamp: timestampObj,
        noteCategory: category,
        assignedUsers: owners || [],
        assignedDepartments: departments || [],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      // Reload notes to update state
      await loadNotes(timestamp);
      return true;
    } catch (error) {
      console.error('Error adding individual note:', error);
      throw error;
    }
  };

  const deleteIndividualNote = async (noteId: string, timestamp: string) => {
    try {
      await deleteDoc(doc(db, COLLECTION_NAMES.L10_NOTES, noteId));
      // Reload notes to update state
      await loadNotes(timestamp);
      return true;
    } catch (error) {
      console.error('Error deleting individual note:', error);
      throw error;
    }
  };

  const deleteMeeting = async (timestamp: string) => {
    try {
      const batch = writeBatch(db);
      const timestampObj = Timestamp.fromDate(new Date(timestamp));

      // Delete from all collections
      const collections = [
        COLLECTION_NAMES.L10_NOTES,
        COLLECTION_NAMES.MEETING_OFF_TRACK_METRICS,
        COLLECTION_NAMES.MEETING_ISSUES,
        COLLECTION_NAMES.MEETING_ROCK_DISCUSSIONS,
        COLLECTION_NAMES.WINS_EXCITED,
        COLLECTION_NAMES.ACTIVE_MEETINGS,
      ];

      for (const collectionName of collections) {
        const q = query(
          collection(db, collectionName),
          where('meetingTimestamp', '==', timestampObj)
        );
        const snapshot = await getDocs(q);
        snapshot.docs.forEach((doc) => {
          batch.delete(doc.ref);
        });
      }

      await batch.commit();
      await loadSavedMeetings();
    } catch (error) {
      console.error('Error deleting meeting:', error);
      throw error;
    }
  };

  const saveOffTrackMetrics = async (
    timestamp: string,
    metricIds: string[]
  ) => {
    try {
      const batch = writeBatch(db);
      const timestampObj = Timestamp.fromDate(new Date(timestamp));

      // Delete existing off-track metrics for this meeting
      const existingQuery = query(
        collection(db, COLLECTION_NAMES.MEETING_OFF_TRACK_METRICS),
        where('meetingTimestamp', '==', timestampObj)
      );
      const existingSnapshot = await getDocs(existingQuery);
      existingSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });

      // Add new off-track metrics
      if (metricIds.length > 0) {
        metricIds.forEach((metricId) => {
          const newMetricRef = doc(collection(db, COLLECTION_NAMES.MEETING_OFF_TRACK_METRICS));
          batch.set(newMetricRef, {
            meetingTimestamp: timestampObj,
            metricId,
            createdAt: Timestamp.now(),
          });
        });
      }

      await batch.commit();
    } catch (error) {
      console.error('Error saving off-track metrics:', error);
      throw error;
    }
  };

  return {
    notes,
    structuredNotes,
    savedMeetings,
    loading,
    loadingMeetings,
    saveNotes,
    addIndividualNote,
    deleteIndividualNote,
    deleteMeeting,
    loadSavedMeetings,
    saveOffTrackMetrics,
  };
}

function formatMeetingDisplay(date: string, timestamp: string): string {
  const [year, month, day] = date.split('-');
  const dateStr = `${month}/${day}/${year}`;

  const meetingTime = new Date(timestamp);
  const timeStr = meetingTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${dateStr} at ${timeStr}`;
}

